<script>
import { mapActions, mapGetters } from 'vuex';
import PasswordSetupForm from '@/components/auth/PasswordSetupForm.vue';
import AuthService from '@/shared/services/auth.service';
import notificationService from '@/shared/services/notification.service';

/**
 * La sortie d'un mot de passe temporaire.
 *
 * Le compte est déjà connecté — c'est le mot de passe reçu par courriel qui
 * l'a ouvert — mais l'API ne lui répond plus que sur cette route tant qu'il
 * n'a pas choisi le sien. L'écran n'est donc pas une formalité : c'est la
 * seule chose que la plateforme lui montre.
 *
 * Le mot de passe actuel est redemandé plutôt que gardé en mémoire depuis la
 * page de connexion : une session ouverte prouve l'accès au poste, pas
 * l'identité, et un mot de passe en clair ne doit survivre à aucun
 * rechargement.
 */
export default {
  name: 'ChangePassword',
  components: { PasswordSetupForm },
  data() {
    return {
      processing: false,
      errorMessage: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['mustChangePassword']),

    subtitle() {
      return this.mustChangePassword
        ? 'Le mot de passe reçu par e-mail est temporaire : choisissez le vôtre pour accéder à la plateforme.'
        : 'Choisissez un nouveau mot de passe pour votre compte.';
    },
  },
  methods: {
    ...mapActions('auth', ['passwordChanged', 'logout']),

    /**
     * L'API répond en anglais (`Invalid credentials`) ou en français selon le
     * refus : le statut décide du message, le corps ne sert qu'à les
     * distinguer.
     */
    errorFor(err) {
      if (!err.response) {
        return 'Serveur injoignable. Veuillez réessayer dans un instant.';
      }

      if (err.response.status === 401) {
        return 'Le mot de passe actuel est incorrect.';
      }

      return (
        err.response.data?.message ||
        'Le mot de passe n’a pas pu être modifié. Veuillez réessayer.'
      );
    },

    async handleSubmit(password, currentPassword) {
      this.errorMessage = null;
      this.processing = true;

      try {
        await AuthService.changePassword(currentPassword, password);

        // Les jetons restent valables — c'est le même compte — mais
        // l'utilisateur stocké porte encore le drapeau, et c'est lui que lit
        // le garde de navigation.
        this.passwordChanged();

        notificationService.toastSuccess('Votre mot de passe a été modifié.');
        this.$router.push({ path: '/' });
      } catch (err) {
        this.errorMessage = this.errorFor(err);
      } finally {
        this.processing = false;
      }
    },

    async handleLogout() {
      await this.logout();
      this.$router.push({ name: 'login' });
    },
  },
};
</script>

<template>
  <PasswordSetupForm
    title="Choisissez votre mot de passe"
    :subtitle="subtitle"
    submit-label="Enregistrer le mot de passe"
    success-title="Mot de passe modifié !"
    success-body="Votre mot de passe a été mis à jour."
    current-password-label="Mot de passe actuel (reçu par e-mail)"
    :requires-code="false"
    :processing="processing"
    :api-error="errorMessage"
    @submit="handleSubmit"
  >
    <!-- Pas de « retour à la connexion » : le compte y est déjà. La seule
         autre issue est de fermer la session. -->
    <template #footnote>
      <button
        type="button"
        class="auth-form__link auth-form__link--button"
        data-testid="change-password-logout"
        @click="handleLogout"
      >
        Se déconnecter
      </button>
    </template>
  </PasswordSetupForm>
</template>
